import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  startOfMonth,
  startOfWeek,
  subWeeks,
} from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import { eventsService } from '@/services/events.service';
import type { CalendarEvent, CalendarEventInstance } from './types';

/** Local-time day key used to bucket instances onto grid cells. */
export function dayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** The 42-cell month grid window (Sunday-start weeks). */
export function gridWindow(month: Date): { gridStart: Date; gridEnd: Date } {
  const gridStart = startOfWeek(startOfMonth(month));
  const gridEnd = endOfWeek(endOfMonth(month));
  return { gridStart, gridEnd };
}

function stripGroupPrefix(title: string, groupName: string): string {
  const prefix = `${groupName}: `;
  return title.startsWith(prefix) ? title.slice(prefix.length) : title;
}

/**
 * Expand one backend event into the occurrences that fall inside the window.
 *
 * The backend stores recurring events as a single row with recurrence_rule
 * 'weekly' | 'biweekly' | 'monthly' and does NOT materialize instances, so we
 * roll the master start forward here. Known v1 gap: the backend query filters
 * on start_time >= startDate, so masters older than the 6-week fetch pad
 * (notably monthly series) drop out of view — acceptable for now.
 */
function expandEvent(
  event: CalendarEvent,
  organizationId: string,
  windowStart: Date,
  windowEnd: Date
): CalendarEventInstance[] {
  const masterStart = new Date(event.start);
  const masterEnd = event.end ? new Date(event.end) : null;
  const durationMs = masterEnd ? masterEnd.getTime() - masterStart.getTime() : 0;
  const title = stripGroupPrefix(event.title, event.extendedProps.group_name);

  const makeInstance = (start: Date, isVirtual: boolean): CalendarEventInstance => ({
    id: event.id,
    organizationId,
    instanceKey: `${event.id}:${dayKey(start)}`,
    title,
    occurrenceStart: start,
    occurrenceEnd: masterEnd ? new Date(start.getTime() + durationMs) : null,
    allDay: event.allDay,
    isVirtual,
    groupId: event.extendedProps.group_id,
    groupName: event.extendedProps.group_name,
    location: event.extendedProps.location,
    recurrenceRule: event.extendedProps.recurrence_rule,
    attachmentCount: event.extendedProps.attachment_count ?? 0,
    rsvpCounts: event.extendedProps.rsvp_counts,
  });

  const rule = event.extendedProps.recurrence_rule;
  const inWindow = (d: Date) => d >= windowStart && d <= windowEnd;

  if (rule !== 'weekly' && rule !== 'biweekly' && rule !== 'monthly') {
    // Single events (and unknown rule strings): master occurrence only.
    return inWindow(masterStart) ? [makeInstance(masterStart, false)] : [];
  }

  const advance = (d: Date): Date =>
    rule === 'weekly' ? addWeeks(d, 1) : rule === 'biweekly' ? addWeeks(d, 2) : addMonths(d, 1);

  const instances: CalendarEventInstance[] = [];
  let cursor = masterStart;
  let isVirtual = false;
  // Hard cap guards against pathological loops (e.g. weekly over a huge pad).
  for (let i = 0; i < 80 && !isBefore(windowEnd, cursor); i++) {
    if (inWindow(cursor)) instances.push(makeInstance(cursor, isVirtual));
    cursor = advance(cursor);
    isVirtual = true;
  }
  return instances;
}

/** What the user has hidden via the calendar sidebar. */
export interface HiddenCalendars {
  orgIds: Set<string>;
  groupIds: Set<string>;
}

/** One org section in the calendar sidebar. */
export interface SidebarOrg {
  id: string;
  name: string;
  /** Member groups ∪ groups discovered in event data (events are org-visible). */
  groups: Array<{ id: string; name: string }>;
  /** Org is paused — its events are omitted from the fetch; show a chip. */
  locked?: boolean;
}

export interface CalendarData {
  /** Instances bucketed by local day key ('yyyy-MM-dd'), sorted within day. */
  eventsByDay: Map<string, CalendarEventInstance[]>;
  /** Sidebar tree: every active org with its known groups. */
  sidebarOrgs: SidebarOrg[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Fetch + shape calendar events for the month grid, across EVERY active org
 * the user belongs to (one org-pinned request each; a failing org degrades
 * to empty rather than killing the calendar). Group/org filtering happens
 * client-side so one cached payload serves every filter combination.
 */
export function useCalendarEvents(month: Date, hidden: HiddenCalendars | null): CalendarData {
  const { organizations } = useAuth();
  const { isOrgLocked } = useOrgLockState();

  const activeOrgs = useMemo(
    () => organizations.filter((o) => o.status === 'active'),
    [organizations]
  );
  // Paused orgs 403 on event reads — omit them from the fetch, but keep them in
  // the sidebar (chipped) so the calendar list still reflects every org.
  const fetchableOrgs = useMemo(
    () => activeOrgs.filter((o) => !isOrgLocked(o.organization_id)),
    [activeOrgs, isOrgLocked]
  );
  const orgIdsKey = fetchableOrgs
    .map((o) => o.organization_id)
    .sort()
    .join(',');

  const { gridStart, gridEnd } = gridWindow(month);
  const monthKey = format(month, 'yyyy-MM');

  const query = useQuery({
    queryKey: ['calendar', monthKey, orgIdsKey],
    enabled: fetchableOrgs.length > 0,
    queryFn: async () => {
      const startDate = subWeeks(gridStart, 6).toISOString();
      // endOfWeek returns 23:59:59.999 local; add a day of slack for TZ skew
      const endDate = addDays(gridEnd, 1).toISOString();
      const results = await Promise.allSettled(
        fetchableOrgs.map(async (org) => ({
          orgId: org.organization_id,
          events: await eventsService.getCalendarEvents(
            startDate,
            endDate,
            undefined,
            org.organization_id
          ),
        }))
      );
      return results
        .filter(
          (r): r is PromiseFulfilledResult<{ orgId: string; events: CalendarEvent[] }> =>
            r.status === 'fulfilled'
        )
        .map((r) => r.value);
    },
  });

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEventInstance[]>();
    if (!query.data) return map;

    for (const { orgId, events } of query.data) {
      if (hidden?.orgIds.has(orgId)) continue;
      for (const event of events) {
        for (const instance of expandEvent(event, orgId, gridStart, gridEnd)) {
          if (hidden?.groupIds.has(instance.groupId)) continue;
          const key = dayKey(instance.occurrenceStart);
          const list = map.get(key);
          if (list) list.push(instance);
          else map.set(key, [instance]);
        }
      }
    }

    for (const list of map.values()) {
      list.sort((a, b) => {
        if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
        return a.occurrenceStart.getTime() - b.occurrenceStart.getTime();
      });
    }
    return map;
    // gridStart/gridEnd derive from month; monthKey covers them
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, monthKey, hidden]);

  const sidebarOrgs = useMemo<SidebarOrg[]>(() => {
    const discovered = new Map<string, Map<string, string>>();
    for (const { orgId, events } of query.data ?? []) {
      const groups = discovered.get(orgId) ?? new Map<string, string>();
      for (const event of events) {
        groups.set(event.extendedProps.group_id, event.extendedProps.group_name);
      }
      discovered.set(orgId, groups);
    }
    return activeOrgs.map((org) => {
      const byId = new Map<string, string>();
      for (const g of org.groups ?? []) byId.set(g.id, g.name);
      for (const [id, name] of discovered.get(org.organization_id) ?? []) {
        if (!byId.has(id)) byId.set(id, name);
      }
      return {
        id: org.organization_id,
        name: org.organization.name,
        groups: [...byId.entries()].map(([id, name]) => ({ id, name })),
        locked: isOrgLocked(org.organization_id),
      };
    });
  }, [activeOrgs, query.data, isOrgLocked]);

  return {
    eventsByDay,
    sidebarOrgs,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
